import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import type { PriceBookSku, ServiceDefinition } from '../../lib/pricebook/pricebook';
import { getServiceBySlug, getSkuById } from '../../lib/pricebook/pricebook';
import CardStack3D, { type Plan } from '../../components/CardStack3D';
import MobileSnapshotFab from '../../components/funnel/MobileSnapshotFab';
import ShareCheckoutLinkModal from '../../components/funnel/ShareCheckoutLinkModal';
import { useFunnelCart } from '../../context/FunnelCartProvider';
import { usePricebookCatalogSync } from '../../hooks/usePricebookCatalogSync';
import {
  readCheckoutLaneSelections,
  upsertCheckoutLaneSelection,
} from '../../lib/funnel/checkoutLaneSelections';
import {
  buildSharedServiceConfigUrl,
  parseSharedServiceConfigToken,
} from '../../lib/funnel/shareServiceConfig';
import { gradientPillCss } from '../../styles/gradientPill';
import {
  canSelectLane,
  getLaneRuleForTier,
  getTierMeta,
  readServiceLaneOptions,
  readTierAllowedLaneIds,
  sanitizeLaneSelection,
  type LaneType,
} from '../../lib/services/managedMarketingLaneRules';
import { getTierStackCardTheme } from '../../lib/services/tierCardThemes';
import FunnelShell from './FunnelShell';

type AddOnFamily = {
  readonly parentCode: string;
  readonly parentName: string;
  readonly variations: readonly PriceBookSku[];
};

type ExclusionItem = {
  readonly code: string;
  readonly name: string;
  readonly description: string;
};

function haveSameIds(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = new Set(a);
  if (left.size !== new Set(b).size) return false;
  return b.every((value) => left.has(value));
}

function readCommittedLaneSelectionsByTierSku(
  allowedTierSkuIds: readonly string[],
): Record<string, readonly string[]> {
  const allowed = new Set(allowedTierSkuIds);
  const next: Record<string, readonly string[]> = {};

  for (const entry of readCheckoutLaneSelections()) {
    if (!allowed.has(entry.tierSkuId)) continue;
    next[entry.tierSkuId] = [...entry.laneIds];
  }

  return next;
}

const CONSISTENT_FEATURES = Object.freeze([
  {
    internalLabel: 'Onboarding & Discovery',
    publicLabel: 'Strategic Onboarding',
    details:
      'Structured intake to understand goals, target audiences, service lines, geography, constraints, approvals, and existing assets.',
  },
  {
    internalLabel: 'Audit & Baseline Review',
    publicLabel: 'Audit & Baseline',
    details:
      'Initial review of current performance, digital presence, and channel readiness to identify gaps, quick wins, and priorities.',
  },
  {
    internalLabel: '90-Day Roadmap & Backlog',
    publicLabel: '90-Day Marketing Plan',
    details:
      'A rolling 90-day plan with prioritized initiatives, focus areas, and execution backlog aligned to business goals and KPIs.',
  },
  {
    internalLabel: 'Measurement Setup & Validation',
    publicLabel: 'Analytics & Conversion Setup',
    details:
      'Validation of GA4, GTM, and key conversion tracking so core actions such as calls, forms, and inquiries can be measured accurately.',
  },
  {
    internalLabel: 'UTM Governance',
    publicLabel: 'Campaign Tracking Standards',
    details:
      'Standardized UTM naming conventions and campaign tracking rules to keep reporting clean and consistent.',
  },
  {
    internalLabel: 'Account Management / Client Success',
    publicLabel: 'Ongoing Account Management',
    details:
      'Ongoing alignment, prioritization, approvals, decision support, and communication with the client throughout the engagement.',
  },
  {
    internalLabel: 'Alignment Meetings',
    publicLabel: 'Regular Strategy & Priority Meetings',
    details:
      'Recurring meetings used to review priorities, make decisions, and keep work aligned with goals.',
  },
  {
    internalLabel: 'Monthly Performance Reporting',
    publicLabel: 'Monthly Reporting',
    details:
      'KPI reporting, work completed, performance review, and next-step planning.',
  },
] as const);

const STEP_ITEMS = Object.freeze([
  { step: 1, title: 'Choose Tier' },
  { step: 2, title: 'Pick Lanes' },
  { step: 3, title: 'Confirm Rules' },
  { step: 4, title: 'Optional Add-ons' },
  { step: 5, title: 'Finalize + Checkout' },
] as const);

const futureTitleTilt = css`
  font-family: var(--font-future);
  display: inline-block;
  transform: rotate(-1.5deg);
  transform-origin: left center;
  letter-spacing: 0.01em;
`;

const profileEnterFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(26px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const profileExitToLeft = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-26px);
  }
`;

const blimpGlide = keyframes`
  0% {
    transform: translate3d(0, 0, 0) rotate(-2deg);
  }
  20% {
    transform: translate3d(12px, -10px, 0) rotate(1deg);
  }
  45% {
    transform: translate3d(22px, 4px, 0) rotate(2.4deg);
  }
  70% {
    transform: translate3d(8px, 14px, 0) rotate(0.8deg);
  }
  100% {
    transform: translate3d(0, 0, 0) rotate(-2deg);
  }
`;

const PROFILE_ITEM_TRANSITION_MS = 320;

function safeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

const Layout = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: minmax(0, 1fr) 280px;

  @media (max-width: 1300px) {
    grid-template-columns: minmax(0, 1fr);
  }

  @media (max-width: 1080px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const LeftStepRail = styled.aside<{ $hidden?: boolean }>`
  border: none;
  border-radius: 16px;
  background: transparent;
  padding: 0.75rem;
  display: grid;
  gap: 0.65rem;
  position: fixed;
  left: 1.1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 176px;
  height: fit-content;
  z-index: 40;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
  transition: opacity 180ms ease;

  @media (max-width: 1360px) {
    left: 0.75rem;
    width: 164px;
  }

  @media (max-width: 1180px) {
    display: none;
  }
`;

const LeftStepList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.5rem;
`;

const LeftStepButton = styled.button<{ $active: boolean }>`
  width: 100%;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? '#E0A15E' : theme.colors.gray200};
  border-radius: 12px;
  padding: 0.6rem;
  text-align: left;
  display: grid;
  gap: 0.14rem;
  background: ${({ theme, $active }) =>
    $active ? 'rgba(224, 161, 94, 0.12)' : theme.colors.gray050};
  color: ${({ theme }) => theme.colors.gray900};
  cursor: pointer;
`;

const LeftStepLabel = styled.span<{ $active?: boolean }>`
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme, $active }) => ($active ? '#ffffff' : theme.colors.gray700)};
`;

const LeftStepTitle = styled.span<{ $active?: boolean }>`
  font-size: 0.92rem;
  font-weight: 800;
  color: ${({ theme, $active }) => ($active ? '#E0A15E' : theme.colors.gray900)};
`;

const Main = styled.section`
  display: grid;
  gap: 4.5rem;

  @media (max-width: 960px) {
    gap: 3rem;
  }
`;

const Hero = styled.header`
  // border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 18px;
  // background: ${({ theme }) => theme.colors.gray050};
  padding: 1rem 1rem 1.5rem;
  color: #E0A15E;
  display: grid;
  gap: 0.65rem;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(2.2rem, 6vw, 4.4rem);
  font-family: var(--font-future);
  display: inline-block;
  transform-origin: left center;
  letter-spacing: 0.01em;
`;


const StepSection = styled.section`
  border: none;
  border-radius: 18px;
  background: transparent;
  padding: 1rem;
  display: grid;
  gap: 0.9rem;
  min-height: calc(100svh - 96px);
  scroll-margin-top: 88px;

  @media (max-width: 1080px) {
    min-height: auto;
  }
`;

const IntroStepSection = styled(StepSection)`
  min-height: auto;
  align-content: start;
`;

const CompactStepSection = styled(StepSection)`
  min-height: auto;
  align-content: start;
`;

const StepSplitLayout = styled.div`
  display: grid;
  gap: 1rem;
  align-items: start;

  @media (min-width: 1080px) {
    grid-template-columns: minmax(300px, 380px) minmax(0, 1fr);
    gap: 1.5rem;
  }
`;

const StepInteractiveColumn = styled.div`
  display: grid;
  gap: 0.9rem;
  min-width: 0;
`;

const StepHeader = styled.header`
  display: grid;
  gap: 0.3rem;
`;

const StepNarrative = styled(StepHeader)`
  @media (min-width: 1080px) {
    width: min(100%, 360px);
  }
`;

const StepHeading = styled.h2`
  margin: 0;
  display: flex;
  gap: 0.55rem;
  align-items: center;
  font-size: clamp(1.28rem, 1.9vw, 1.65rem);
`;

const StepHeadingText = styled.span`
  ${futureTitleTilt};
`;

const StepBadge = styled.span`
  display: inline-flex;
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => `${theme.colors.primary}12`};
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const StepIntroLayout = styled.div`
  display: grid;
  gap: 1.25rem;
  align-items: center;

  @media (min-width: 960px) {
    grid-template-columns: minmax(320px, 420px) minmax(320px, 480px);
    gap: 2rem;
    justify-content: space-between;
  }
`;

const StepIntroHeader = styled(StepHeader)`
  order: 1;

  @media (min-width: 960px) {
    order: 1;
    align-content: center;
    justify-self: end;
    width: min(100%, 420px);
  }
`;

const StackWrap = styled.div`
  width: min(480px, 100%);
  margin-inline: auto;
`;

const StepIntroStack = styled(StackWrap)`
  order: 2;

  @media (min-width: 960px) {
    order: 2;
    margin-inline: 0;
    justify-self: start;
  }
`;

const TierActionRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  ${gradientPillCss};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  border: 0;
`;

const ActionStack = styled.div`
  display: grid;
  gap: 0.42rem;

  > * {
    width: 100%;
  }
`;

const ShareButton = styled.button`
  border: 1px solid rgba(224, 161, 94, 0.58);
  background: transparent;
  color: #e0a15e;
  border-radius: 10px;
  padding: 0.5rem 0.72rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: rgba(224, 161, 94, 0.85);
    background: rgba(224, 161, 94, 0.08);
  }
`;

const SlotsTag = styled.span`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 999px;
  padding: 0.24rem 0.65rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray700};
`;

const SlotsTagInline = styled(SlotsTag)`
  justify-self: start;
`;

const SelectedTierMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
`;

const SelectedTierName = styled.div`
  font-size: 0.98rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
`;

const LaneGrid = styled.div`
  display: grid;
  gap: 0.65rem;
  grid-template-columns: repeat(auto-fill, minmax(225px, 1fr));
`;

const LaneTypeSection = styled.section`
  display: grid;
  gap: 0.55rem;
`;

const LaneTypeHeading = styled.h3`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.colors.gray700};
`;

const LaneButton = styled.button<{ $selected: boolean }>`
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.primary : theme.colors.gray200};
  background: ${({ theme, $selected }) =>
    $selected ? `${theme.colors.primary}15` : theme.colors.gray050};
  border-radius: 12px;
  padding: 0.7rem;
  text-align: left;
  display: grid;
  gap: 0.35rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.gray900};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.46;
  }
`;

const AddOnGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: stretch;
  justify-content: flex-start;
`;

const AddOnFamilyCard = styled.article`
  border: 1px solid rgba(24, 24, 27, 0.18);
  border-radius: 14px;
  padding: 0.9rem 0.95rem;
  background: #969696;
  color: #18181b;
  display: grid;
  gap: 0.5rem;
  align-content: space-between;
  aspect-ratio: 15 / 10;
  flex: 0 1 385px;
  width: min(100%, 385px);
  max-width: 385px;
`;

const AddOnChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.38rem;
  align-self: start;
`;

const AddOnChip = styled.button<{ $selected: boolean }>`
  border: 1px ${({ $selected }) => ($selected ? 'solid' : 'dashed')}
    ${({ $selected }) =>
      $selected ? 'rgba(24, 24, 27, 0.96)' : 'rgba(24, 24, 27, 0.62)'};
  background: ${({ $selected }) => ($selected ? '#18181b' : 'transparent')};
  border-radius: 8px;
  padding: 0.36rem 0.68rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ $selected }) => ($selected ? '#a64c44' : '#18181b')};
  cursor: pointer;

  &:hover {
    border-color: rgba(24, 24, 27, 0.92);
  }
`;

const AddOnSelectionPanel = styled.div`
  padding: 0.15rem 0 0;
  display: grid;
  gap: 0.35rem;
`;

const AddOnTitle = styled.strong`
  font-size: 1rem;
  line-height: 1.25;
  color: #18181b;
`;

const AddOnDescription = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: rgba(24, 24, 27, 0.82);
`;

const LaneMeta = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.gray700};
`;

const MiniGrid = styled.div`
  --feature-card-height: clamp(30rem, 46vw, 36rem);
  display: grid;
  gap: 0.65rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  grid-auto-rows: var(--feature-card-height);
  align-items: stretch;

  @media (max-width: 720px) {
    grid-auto-rows: auto;
  }
`;

const FeatureCardStack = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  justify-items: center;
  gap: 1rem;
  min-width: 0;
  min-height: 100%;
  height: 100%;
  align-self: stretch;
`;

const InclusionFeatureCard = styled.article`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  align-content: start;
  gap: 1.15rem;
  width: 100%;
  height: 100%;
  min-height: 100%;
  border-radius: 24px;
  padding: 1rem;
  background: #658963;
  color: #18181b;
  box-shadow: 0 22px 42px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const FeatureStatusIcon = styled.img`
  width: 42px;
  height: 42px;
  object-fit: contain;
  filter: drop-shadow(0 12px 20px rgba(0, 0, 0, 0.18));
`;

const InclusionFeatureEyebrow = styled.span`
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.92);
`;

const InclusionFeatureGrid = styled.div`
  display: grid;
  align-content: start;
  gap: 0.65rem;
  min-height: 0;
  overflow: auto;
  padding-right: 0.18rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(24, 24, 27, 0.38) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(24, 24, 27, 0.38);
    border-radius: 999px;
  }
`;

const InclusionFeatureTile = styled.button<{ $selected: boolean }>`
  border: 1px dashed rgba(24, 24, 27, 0.72);
  border-radius: 14px;
  padding: 0.82rem 0.88rem;
  background: transparent;
  text-align: left;
  display: grid;
  gap: 0.48rem;
  cursor: pointer;
  color: inherit;
  transition:
    transform 140ms ease,
    border-color 140ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(24, 24, 27, 0.92);
  }
`;

const InclusionFeaturePublic = styled.span`
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.25;
`;

const InclusionFeatureHeader = styled.div`
  display: grid;
  gap: 0.18rem;
`;

const InclusionFeatureDetail = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.55;
  color: inherit;
`;

const ExclusionFeatureCard = styled(InclusionFeatureCard)`
  background: #a64c44;
  color: #18181b;
`;

const ProfileFocusWrap = styled.div`
  display: grid;
  gap: 0.7rem;
  padding: 0.15rem 0.15rem 0.05rem;
  width: min(680px, 100%);
  justify-self: center;
`;

const ProfileFocusStage = styled.div`
  position: relative;
  min-height: 3.5rem;
  overflow: hidden;
`;

const ProfileFocusItem = styled.div<{ $phase: 'idle' | 'entering' | 'exiting' }>`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  margin: 0;
  color: ${({ theme }) => theme.colors.gray900};
  font-size: clamp(1.15rem, 2.7vw, 1.52rem);
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.01em;
  text-wrap: balance;
  text-align: center;

  ${({ $phase }) => {
    if ($phase === 'entering') {
      return css`
        animation: ${profileEnterFromRight} ${PROFILE_ITEM_TRANSITION_MS}ms ease forwards;
      `;
    }
    if ($phase === 'exiting') {
      return css`
        animation: ${profileExitToLeft} ${PROFILE_ITEM_TRANSITION_MS}ms ease forwards;
      `;
    }
    return css`
      animation: none;
    `;
  }}
`;

const ProfileFocusDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.44rem;
`;

const ProfileFocusDot = styled.button<{ $active: boolean }>`
  width: 9px;
  height: 9px;
  border-radius: 999px;
  border: 0;
  padding: 0;
  cursor: pointer;
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary : `${theme.colors.gray200}`};
`;

const SummaryList = styled.ul`
  margin: 0;
  padding-left: 1rem;
  display: grid;
  gap: 0.2rem;
  color: ${({ theme }) => theme.colors.gray700};
`;

const RightColumn = styled.aside`
  display: grid;
  gap: 0.75rem;
  margin-left: 0.75rem;
  align-self: start;
  position: sticky;
  top: 88px;
  height: fit-content;

  @media (max-width: 1080px) {
    display: none;
  }
`;

const SidePanel = styled.aside`
  border: none;
  border-radius: 18px;
  background: transparent;
  padding: 1rem;
  display: grid;
  gap: 0.75rem;
`;

const SidePanelTitle = styled.h3`
  margin: 0;
  ${futureTitleTilt};
`;

const OtherServicesRailCard = styled.section`
  border: none;
  border-radius: 18px;
  background: transparent;
  padding: 0.85rem;
  display: grid;
  gap: 0.65rem;
`;

const RailSectionTitle = styled.strong`
  ${futureTitleTilt};
`;

const OtherServicesRailList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 0.5rem;
  min-height: 100%;
`;

const OtherServiceCardLink = styled(Link)<{ $variant: 'platform' | 'assets' }>`
  text-decoration: none;
  border: none;
  border-radius: 14px;
  color: #ffffff;
  padding: 0.7rem;
  display: grid;
  gap: 0.4rem;
  align-content: end;
   width: auto;
  height: 100%;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.24);
  background: ${({ $variant }) =>
    $variant === 'platform'
      ? 'linear-gradient(140deg, #1f3160 0%, #1a7fbe 48%, #69d2ff 100%)'
      : 'linear-gradient(140deg, #153042 0%, #1d7a74 48%, #87d37c 100%)'};
`;

const OtherServiceCardTitle = styled.strong`
  font-size: 0.92rem;
  line-height: 1.2;
`;

const OtherServiceCardTeaser = styled.span`
  font-size: 0.78rem;
  line-height: 1.25;
  color: rgba(255, 255, 255, 0.92);
`;

const UpsellShowcaseSection = styled.section`
  position: relative;
  isolation: isolate;
  overflow: clip;
  margin-top: 1.25rem;
  min-height: 52svh;
  width: 100%;
  max-width: 100%;
  margin-bottom: -2rem;
  background: #efecf0;
  color: #0f172a;
  padding: clamp(1.1rem, 2.2vw, 2rem) clamp(1rem, 2.8vw, 2rem);
  display: flex;
  align-items: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(31, 41, 55, 0.8) 1px, transparent 1px),
      linear-gradient(90deg, rgba(31, 41, 55, 0.8) 1px, transparent 1px);
    background-size: 54px 54px;
    opacity: 0.22;
    pointer-events: none;
    z-index: 0;
    mask-image: radial-gradient(circle at 62% 46%, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.72) 38%, rgba(0, 0, 0, 0.24) 72%, transparent 100%);
  }
`;

const UpsellShowcaseInner = styled.div`
  position: relative;
  z-index: 1;
  width: min(1320px, 100%);
  margin: 0 auto;
  display: grid;
  gap: clamp(1rem, 2.2vw, 2rem);
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: stretch;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const UpsellShowcaseCopy = styled.article`
  border-radius: 18px;
  background: transparent;
  border: none;
  padding: clamp(1rem, 2vw, 1.4rem);
  display: grid;
  gap: 0.9rem;
  align-content: center;
`;

const UpsellShowcaseTitle = styled.h3`
  margin: 0;
  font-size: clamp(1.35rem, 2.6vw, 2.1rem);
  line-height: 1.08;
  letter-spacing: -0.01em;
  ${futureTitleTilt};
`;

const UpsellShowcaseLead = styled.p`
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.5;
  color: rgba(15, 23, 42, 0.85);
`;

const UpsellShowcaseCards = styled.div`
  display: grid;
  gap: 0.7rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const UpsellShowcaseCard = styled.article`
  border-radius: 14px;
  border: none;
  background: transparent;
  padding: 0.9rem;
  display: grid;
  gap: 0.45rem;
`;

const UpsellShowcaseActions = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const UpsellCtaPrimary = styled(Link)`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  ${gradientPillCss};
`;

const UpsellShowcaseStackWrap = styled.aside`
  position: relative;
  isolation: isolate;
  border-radius: 22px;
  background: transparent;
  border: none;
  width: 100%;
  min-height: 100%;
  min-height: 440px;
  padding: clamp(0.7rem, 1.6vw, 1rem);
  display: grid;
  align-items: center;
  justify-items: center;

  @media (max-width: 1080px) {
    min-height: 390px;
  }
`;

const UpsellShowcaseImage = styled.img`
  position: relative;
  z-index: 1;
  display: block;
  width: min(100%, 720px);
  max-width: 100%;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 26px 38px rgba(15, 23, 42, 0.18));
  transform-origin: 50% 58%;
  animation: ${blimpGlide} 10s ease-in-out infinite;

  @media (max-width: 1080px) {
    width: min(100%, 560px);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

function readServiceExclusionsCatalog(service: ServiceDefinition): readonly ExclusionItem[] {
  const metadata = (service.metadata as Record<string, unknown> | undefined) ?? {};
  const rawCatalog = metadata.exclusionsCatalog;
  if (Array.isArray(rawCatalog)) {
    return rawCatalog
      .flatMap((entry) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return [];
        const raw = entry as Record<string, unknown>;
        const name = safeString(raw.name);
        if (!name) return [];
        const code = safeString(raw.code).trim().toUpperCase() || name.toUpperCase().replace(/\s+/g, '_');
        return [
          {
            code,
            name,
            description: safeString(raw.description),
          },
        ];
      });
  }

  const legacy = metadata.exclusions;
  if (!Array.isArray(legacy)) return [];
  return legacy
    .map((entry) => safeString(entry))
    .filter(Boolean)
    .map((name) => ({
      code: name.toUpperCase().replace(/\s+/g, '_'),
      name,
      description: '',
    }));
}

function getDeliveryHours(tier: PriceBookSku): string {
  const hours = getTierMeta(tier).deliveryHours as { min?: number; max?: number } | undefined;
  if (!hours || typeof hours.min !== 'number' || typeof hours.max !== 'number') {
    return 'Not specified';
  }
  return `${hours.min}-${hours.max} hrs/mo`;
}

function getTierNameShort(name: string): string {
  const parts = name.trim().split(' ');
  return parts[parts.length - 1] || name;
}

function getMinimumTermLabel(tier: PriceBookSku): string {
  const minTerm = getTierMeta(tier).minTermMonths;
  const recommendedNote = getTierMeta(tier).recommendedTermUiCopy;
  const base =
    typeof minTerm === 'number'
      ? minTerm === 0
        ? 'No Monthly Contract'
        : `${minTerm} month contract`
      : 'Contract term defined in proposal';
  if (typeof recommendedNote === 'string' && recommendedNote.trim()) {
    return `${base} (${recommendedNote})`;
  }
  return base;
}

function getServiceHoursSummary(tier: PriceBookSku): string {
  const hours = getTierMeta(tier).deliveryHours as { min?: number; max?: number } | undefined;
  if (!hours || typeof hours.min !== 'number' || typeof hours.max !== 'number') {
    return 'Service hours defined in proposal';
  }
  if (hours.min === hours.max) {
    return `${hours.min} service hours per month`;
  }
  return `${hours.min}-${hours.max} service hours per month`;
}

function getLaneSelectionSummary(tier: PriceBookSku): string {
  const activeLanes = getTierMeta(tier).activeLanesIncluded;
  if (typeof activeLanes === 'number' && Number.isFinite(activeLanes)) {
    return `Select up to ${activeLanes} lanes`;
  }
  if (typeof activeLanes === 'string' && activeLanes.trim().toLowerCase() === 'all') {
    return 'Select all available lanes';
  }
  return 'Lane selection defined in proposal';
}

function getCadenceSummary(tier: PriceBookSku): string {
  const rawCadenceType = safeString(getTierMeta(tier).clientSuccessCadenceType);
  const meetingsRaw = getTierMeta(tier).clientSuccessMeetingsPerMonth;
  const legacyCadence = safeString(getTierMeta(tier).clientSuccessCadence);
  const parsedCadence = legacyCadence.match(/^([a-zA-Z ]+)\s*[:|-]\s*(\d+)$/);
  const cadenceKind = (rawCadenceType || (parsedCadence ? parsedCadence[1] : legacyCadence))
    .trim()
    .toLowerCase();
  const meetingsFromCadence = parsedCadence ? Number.parseInt(parsedCadence[2], 10) : null;

  const meetingsFromField =
    typeof meetingsRaw === 'number'
      ? meetingsRaw
      : typeof meetingsRaw === 'string' && meetingsRaw.trim()
        ? Number.parseInt(meetingsRaw, 10)
        : null;

  const meetingsPerMonth =
    typeof meetingsFromField === 'number' && Number.isFinite(meetingsFromField)
      ? meetingsFromField
      : typeof meetingsFromCadence === 'number' && Number.isFinite(meetingsFromCadence)
        ? meetingsFromCadence
        : null;

  if (cadenceKind.includes('weekly')) {
    return 'Account alignment each week';
  }

  if (meetingsPerMonth && meetingsPerMonth > 0) {
    return `${meetingsPerMonth} account alignment meetings per month`;
  }

  if (cadenceKind.includes('monthly')) {
    return 'Monthly account alignment meeting';
  }

  return 'Account alignment cadence defined in proposal';
}

function getContractSummary(tier: PriceBookSku): string {
  const minTerm = getTierMeta(tier).minTermMonths;
  if (typeof minTerm === 'number' && Number.isFinite(minTerm) && minTerm > 0) {
    return `${minTerm} month contract`;
  }
  if (typeof minTerm === 'number' && Number.isFinite(minTerm) && minTerm === 0) {
    return 'No Monthly Contract';
  }
  return 'Contract term defined in proposal';
}

function isRequiredOnboardingAddOn(addOn: PriceBookSku): boolean {
  return typeof addOn.metadata?.requiredForTierSkuId === 'string';
}

function isServiceCatalogAddOn(addOn: PriceBookSku): boolean {
  return addOn.metadata?.addOnSource === 'service_catalog';
}

function getAddOnParentCode(addOn: PriceBookSku): string {
  return typeof addOn.metadata?.addOnParentCode === 'string'
    ? addOn.metadata.addOnParentCode
    : addOn.skuId;
}

function getAddOnParentName(addOn: PriceBookSku): string {
  return typeof addOn.metadata?.addOnParentName === 'string'
    ? addOn.metadata.addOnParentName
    : addOn.name;
}

function getAddOnVariationLabel(addOn: PriceBookSku, parentName: string): string {
  const normalizedParent = parentName.trim().toLowerCase();
  const normalizedName = addOn.name.trim();
  if (normalizedParent && normalizedName.toLowerCase().startsWith(normalizedParent)) {
    const stripped = normalizedName.slice(parentName.length).replace(/^[-:\s/]+/, '').trim();
    if (stripped) return stripped;
  }
  const sourceTierName =
    typeof addOn.metadata?.addOnSourceTierName === 'string'
      ? addOn.metadata.addOnSourceTierName
      : '';
  if (sourceTierName.trim()) return getTierNameShort(sourceTierName);
  return normalizedName;
}

function buildStackPlanFromTier(tier: PriceBookSku): Plan {
  const shortName = getTierNameShort(tier.name);
  const cardTheme = getTierStackCardTheme(shortName);
  return {
    id: tier.skuId,
    name: shortName,
    animationKey: 'digital',
    price: Math.round(tier.amountCents / 100),
    cadence: '/month',
    description: tier.description,
    blurb: getCadenceSummary(tier),
    accent: cardTheme?.titleAccent,
    bg: cardTheme?.bg,
    ink: cardTheme?.ink,
    features: [],
    offerings: [
      { key: 'delivery', label: 'Delivery', value: getDeliveryHours(tier) },
      {
        key: 'lanes',
        label: 'Active lanes',
        value: String(getTierMeta(tier).activeLanesIncluded ?? 'Defined in scope'),
      },
      { key: 'term', label: 'Term', value: getMinimumTermLabel(tier) },
    ],
  };
}

type ManagedMarketingServicePageProps = {
  readonly service: ServiceDefinition;
};

export default function ManagedMarketingServicePage({
  service,
}: ManagedMarketingServicePageProps) {
  usePricebookCatalogSync();
  const location = useLocation();
  const navigate = useNavigate();
  const { items, replaceItems } = useFunnelCart();
  const currentService = getServiceBySlug(service.slug) ?? service;
  const tiers = useMemo(() => [...currentService.tiers], [currentService.tiers]);
  const currentServiceTierSkuIds = useMemo(
    () => new Set(currentService.tiers.map((tier) => tier.skuId)),
    [currentService.tiers],
  );
  const currentServiceSkuIds = useMemo(
    () => new Set([...currentService.tiers, ...currentService.addOns].map((sku) => sku.skuId)),
    [currentService.addOns, currentService.tiers],
  );

  const cartSelectedTierSkuId =
    items.find((item) => currentServiceTierSkuIds.has(item.skuId))?.skuId ?? null;
  const optionalAddOns = useMemo(
    () =>
      currentService.addOns.filter(
        (addOn) => isServiceCatalogAddOn(addOn) && !isRequiredOnboardingAddOn(addOn),
      ),
    [currentService.addOns],
  );
  const optionalAddOnSkuIds = useMemo(
    () => new Set(optionalAddOns.map((addOn) => addOn.skuId)),
    [optionalAddOns],
  );
  const serviceLaneOptions = useMemo(
    () => readServiceLaneOptions(currentService),
    [currentService],
  );
  const sharedConfigToken = useMemo(
    () => new URLSearchParams(location.search).get('config') ?? '',
    [location.search],
  );
  const parsedSharedConfig = useMemo(
    () => (sharedConfigToken ? parseSharedServiceConfigToken(sharedConfigToken) : null),
    [sharedConfigToken],
  );
  const sharedConfigHasLaneSelections = Boolean(parsedSharedConfig?.laneIds?.length);
  const sharedConfigLaneHydrationReady =
    !sharedConfigHasLaneSelections || serviceLaneOptions.length > 0;
  const validSharedConfig = useMemo(() => {
    if (!parsedSharedConfig || parsedSharedConfig.servicePath !== '/services/managed-marketing') {
      return null;
    }

    const tierToShare = tiers.find((tier) => tier.skuId === parsedSharedConfig.tierSkuId);
    if (!tierToShare) return null;

    const validAddOnSkuIds = (parsedSharedConfig.addOnSkuIds ?? []).filter((skuId) =>
      optionalAddOnSkuIds.has(skuId),
    );
    const allowedLaneIds = new Set(readTierAllowedLaneIds(tierToShare, serviceLaneOptions));
    const validLaneIds = (parsedSharedConfig.laneIds ?? []).filter((laneId) =>
      allowedLaneIds.has(laneId),
    );

    return {
      ...parsedSharedConfig,
      addOnSkuIds: validAddOnSkuIds,
      laneIds: validLaneIds,
    };
  }, [optionalAddOnSkuIds, parsedSharedConfig, serviceLaneOptions, tiers]);
  const marketingCartItems = useMemo(
    () =>
      items.filter(
        (item) =>
          currentServiceSkuIds.has(item.skuId) && !currentServiceTierSkuIds.has(item.skuId),
      ),
    [currentServiceSkuIds, currentServiceTierSkuIds, items],
  );
  const cartSelectedOptionalAddOnSkuIds = useMemo(
    () =>
      marketingCartItems
        .filter((item) => optionalAddOnSkuIds.has(item.skuId))
        .map((item) => item.skuId),
    [marketingCartItems, optionalAddOnSkuIds],
  );
  const defaultTierSkuId = useMemo(() => {
    const basicTier =
      tiers.find((tier) => /basic/i.test(tier.name)) ??
      tiers.find((tier) => /basic/i.test(tier.skuId));
    return basicTier?.skuId ?? tiers[0]?.skuId ?? '';
  }, [tiers]);
  const initialCommittedLaneSelectionsByTierSku = useMemo(
    () => readCommittedLaneSelectionsByTierSku(tiers.map((tier) => tier.skuId)),
    [tiers],
  );
  const [focusedTierSkuId, setFocusedTierSkuId] = useState<string>(
    validSharedConfig?.tierSkuId || cartSelectedTierSkuId || defaultTierSkuId || '',
  );
  const [laneSelectionsByTierSku, setLaneSelectionsByTierSku] = useState<
    Record<string, readonly string[]>
  >(() => {
    if (!validSharedConfig?.tierSkuId || !validSharedConfig.laneIds) {
      return { ...initialCommittedLaneSelectionsByTierSku };
    }

    return {
      ...initialCommittedLaneSelectionsByTierSku,
      [validSharedConfig.tierSkuId]: [...validSharedConfig.laneIds],
    };
  });
  const [committedLaneSelectionsByTierSku, setCommittedLaneSelectionsByTierSku] = useState<
    Record<string, readonly string[]>
  >(() => ({ ...initialCommittedLaneSelectionsByTierSku }));
  const [selectedOptionalAddOnSkuIds, setSelectedOptionalAddOnSkuIds] = useState<string[]>(
    validSharedConfig?.addOnSkuIds
      ? [...validSharedConfig.addOnSkuIds]
      : [...cartSelectedOptionalAddOnSkuIds],
  );
  const [activeProfileIndex, setActiveProfileIndex] = useState(0);
  const [profilePhase, setProfilePhase] = useState<'idle' | 'entering' | 'exiting'>('idle');
  const [pendingProfileIndex, setPendingProfileIndex] = useState<number | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [selectedInclusionItem, setSelectedInclusionItem] = useState<string>('');
  const [selectedExclusionItem, setSelectedExclusionItem] = useState<string>('');
  const appliedSharedConfigTokenRef = useRef('');

  const cartSelectedTier = tiers.find((tier) => tier.skuId === cartSelectedTierSkuId) || null;
  const selectedTier = tiers.find((tier) => tier.skuId === focusedTierSkuId) || null;
  const focusedTier = selectedTier;
  const selectedOptionalAddOnCartItems = useMemo(
    () =>
      optionalAddOns.filter((addOn) => selectedOptionalAddOnSkuIds.includes(addOn.skuId)),
    [optionalAddOns, selectedOptionalAddOnSkuIds],
  );
  const optionalAddOnFamilies = useMemo<readonly AddOnFamily[]>(() => {
    const families = new Map<string, AddOnFamily>();
    for (const addOn of optionalAddOns) {
      const parentCode = getAddOnParentCode(addOn);
      const existing = families.get(parentCode);
      if (existing) {
        families.set(parentCode, {
          ...existing,
          variations: [...existing.variations, addOn],
        });
        continue;
      }
      families.set(parentCode, {
        parentCode,
        parentName: getAddOnParentName(addOn),
        variations: [addOn],
      });
    }

    return Array.from(families.values()).map((family) => ({
      ...family,
      variations: [...family.variations].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [optionalAddOns]);
  const stackPlans = useMemo(() => tiers.map((tier) => buildStackPlanFromTier(tier)), [tiers]);
  const initialStackIndex = useMemo(() => {
    const activeSkuId = focusedTierSkuId || defaultTierSkuId;
    const index = stackPlans.findIndex((plan) => plan.id === activeSkuId);
    return index >= 0 ? index : 0;
  }, [defaultTierSkuId, focusedTierSkuId, stackPlans]);
  const workingTier = selectedTier;
  const tierAtGlanceItems = useMemo(
    () =>
      focusedTier
        ? [
            getServiceHoursSummary(focusedTier),
            getLaneSelectionSummary(focusedTier),
            getCadenceSummary(focusedTier),
            getContractSummary(focusedTier),
          ]
        : [],
    [focusedTier],
  );
  const workingTierLaneOptions = useMemo(() => {
    if (!workingTier || !serviceLaneOptions.length) return [];
    const allowedIds = new Set(readTierAllowedLaneIds(workingTier, serviceLaneOptions));
    return serviceLaneOptions.filter((lane) => allowedIds.has(lane.id));
  }, [serviceLaneOptions, workingTier]);
  const workingLaneRule = workingTier
    ? getLaneRuleForTier(workingTier, workingTierLaneOptions)
    : null;
  const visibleLaneSections = useMemo(() => {
    if (!workingLaneRule || !workingTierLaneOptions.length) return [];
    const allowedSet = new Set(workingLaneRule.allowedLaneIds);
    const allowedLanes = workingTierLaneOptions.filter((lane) => allowedSet.has(lane.id));
    const orderedTypes: readonly LaneType[] = ['core', 'advanced', 'specialty'];
    const labelByType: Record<LaneType, string> = {
      core: 'Core lanes',
      advanced: 'Advanced lanes',
      specialty: 'Specialty lanes',
    };

    return orderedTypes
      .map((type) => ({
        type,
        label: labelByType[type],
        lanes: allowedLanes.filter((lane) => lane.type === type),
      }))
      .filter((section) => section.lanes.length > 0);
  }, [workingLaneRule, workingTierLaneOptions]);
  const workingSelectedLaneIds =
    workingTier && workingLaneRule
      ? sanitizeLaneSelection(workingLaneRule, laneSelectionsByTierSku[workingTier.skuId])
      : [];
  const committedLaneIdsForWorkingTier =
    workingTier && workingTierLaneOptions.length
      ? sanitizeLaneSelection(
          getLaneRuleForTier(workingTier, workingTierLaneOptions),
          committedLaneSelectionsByTierSku[workingTier.skuId],
        )
      : [];
  const isFocusedTierInCart =
    Boolean(workingTier) &&
    cartSelectedTier?.skuId === workingTier?.skuId &&
    haveSameIds(cartSelectedOptionalAddOnSkuIds, selectedOptionalAddOnSkuIds) &&
    haveSameIds(committedLaneIdsForWorkingTier, workingSelectedLaneIds);
  const getSanitizedLaneSelectionForTier = useCallback(
    (tier: PriceBookSku): string[] => {
      const allowedLaneIds = new Set(readTierAllowedLaneIds(tier, serviceLaneOptions));
      const tierLaneOptions = serviceLaneOptions.filter((lane) => allowedLaneIds.has(lane.id));
      const tierLaneRule = getLaneRuleForTier(tier, tierLaneOptions);
      return sanitizeLaneSelection(tierLaneRule, laneSelectionsByTierSku[tier.skuId]);
    },
    [laneSelectionsByTierSku, serviceLaneOptions],
  );

  const serviceExclusions = useMemo(
    () => readServiceExclusionsCatalog(currentService),
    [currentService],
  );


  const stepSectionRefs = useRef<Record<number, HTMLElement | null>>({
    1: null,
    2: null,
    3: null,
    4: null,
    5: null,
  });
  const upsellSectionRef = useRef<HTMLElement | null>(null);
  const stepLockUntilRef = useRef<number>(0);
  const [activeStep, setActiveStep] = useState<number>(1);
  const [hideLeftStepRail, setHideLeftStepRail] = useState(false);

  useEffect(() => {
    if (!serviceExclusions.length) {
      if (selectedExclusionItem) setSelectedExclusionItem('');
      return;
    }
    if (!serviceExclusions.some((entry) => entry.code === selectedExclusionItem)) {
      setSelectedExclusionItem('');
    }
  }, [selectedExclusionItem, serviceExclusions]);

  const queueProfileIndex = useCallback(
    (nextIndex: number) => {
      if (!tierAtGlanceItems.length) return;
      if (nextIndex === activeProfileIndex) return;

      if (profilePhase !== 'idle') {
        setActiveProfileIndex(nextIndex);
        setPendingProfileIndex(null);
        setProfilePhase('entering');
        return;
      }

      setPendingProfileIndex(nextIndex);
      setProfilePhase('exiting');
    },
    [activeProfileIndex, profilePhase, tierAtGlanceItems.length],
  );

  useEffect(() => {
    setActiveProfileIndex(0);
    setPendingProfileIndex(null);
    setProfilePhase('entering');
  }, [tierAtGlanceItems.length]);

  useEffect(() => {
    if (tierAtGlanceItems.length <= 1) return;
    const timer = window.setInterval(() => {
      if (profilePhase !== 'idle') return;
      queueProfileIndex((activeProfileIndex + 1) % tierAtGlanceItems.length);
    }, 2600);
    return () => window.clearInterval(timer);
  }, [activeProfileIndex, profilePhase, queueProfileIndex, tierAtGlanceItems.length]);

  useEffect(() => {
    if (profilePhase === 'exiting' && pendingProfileIndex !== null) {
      const timeout = window.setTimeout(() => {
        setActiveProfileIndex(pendingProfileIndex);
        setPendingProfileIndex(null);
        setProfilePhase('entering');
      }, PROFILE_ITEM_TRANSITION_MS);
      return () => window.clearTimeout(timeout);
    }

    if (profilePhase === 'entering') {
      const timeout = window.setTimeout(() => {
        setProfilePhase('idle');
      }, PROFILE_ITEM_TRANSITION_MS);
      return () => window.clearTimeout(timeout);
    }
  }, [pendingProfileIndex, profilePhase]);

  useEffect(() => {
    if (
      sharedConfigToken &&
      validSharedConfig &&
      appliedSharedConfigTokenRef.current !== sharedConfigToken
    ) {
      if (!sharedConfigLaneHydrationReady) {
        return;
      }
      setFocusedTierSkuId(validSharedConfig.tierSkuId);
      setSelectedOptionalAddOnSkuIds([...(validSharedConfig.addOnSkuIds ?? [])]);
      setLaneSelectionsByTierSku((previous) => ({
        ...previous,
        [validSharedConfig.tierSkuId]: [...(validSharedConfig.laneIds ?? [])],
      }));
      appliedSharedConfigTokenRef.current = sharedConfigToken;
      return;
    }

    const hasFocusedTier = tiers.some((tier) => tier.skuId === focusedTierSkuId);
    if (!hasFocusedTier && defaultTierSkuId) {
      setFocusedTierSkuId(defaultTierSkuId);
    }
  }, [
    defaultTierSkuId,
    focusedTierSkuId,
    sharedConfigLaneHydrationReady,
    sharedConfigToken,
    tiers,
    validSharedConfig,
  ]);

  useEffect(() => {
    const updateActiveStep = () => {
      if (Date.now() < stepLockUntilRef.current) {
        return;
      }

      const triggerY = 120;
      let nextActive = 1;
      let bestDistance = Number.POSITIVE_INFINITY;

      for (const item of STEP_ITEMS) {
        const section = stepSectionRefs.current[item.step];
        if (!section) continue;
        const rect = section.getBoundingClientRect();
        const distance = Math.abs(rect.top - triggerY);
        if (distance < bestDistance) {
          bestDistance = distance;
          nextActive = item.step;
        }
      }

      const nearPageBottom =
        window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 8;
      if (nearPageBottom) {
        nextActive = STEP_ITEMS[STEP_ITEMS.length - 1].step;
      }

      setActiveStep((current) => (current === nextActive ? current : nextActive));
    };

    updateActiveStep();
    window.addEventListener('scroll', updateActiveStep, { passive: true });
    window.addEventListener('resize', updateActiveStep);
    return () => {
      window.removeEventListener('scroll', updateActiveStep);
      window.removeEventListener('resize', updateActiveStep);
    };
  }, []);

  useEffect(() => {
    const target = upsellSectionRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setHideLeftStepRail(entry.isIntersecting);
      },
      {
        threshold: 0.08,
      },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  const setStepSectionRef =
    (step: number) =>
    (section: HTMLElement | null): void => {
      stepSectionRefs.current[step] = section;
    };

  const scrollToStep = (step: number) => {
    const section =
      stepSectionRefs.current[step] ||
      document.getElementById(`managed-marketing-step-${step}`);
    if (!section) return;
    const headerOffset = 88;
    const sectionRect = section.getBoundingClientRect();
    const sectionTop = window.scrollY + sectionRect.top;
    const sectionBottom = window.scrollY + sectionRect.bottom;
    const maxScrollableTop =
      document.documentElement.scrollHeight - window.innerHeight;

    let targetTop = sectionTop - headerOffset;
    if (targetTop > maxScrollableTop) {
      targetTop = sectionBottom - window.innerHeight + 24;
    }

    stepLockUntilRef.current = Date.now() + 900;
    window.scrollTo({
      top: Math.max(0, Math.min(targetTop, maxScrollableTop)),
      behavior: 'smooth',
    });
    setActiveStep(step);
  };

  const persistCheckoutLaneSelectionForTier = useCallback(
    (tier: PriceBookSku | null | undefined) => {
      if (!tier) return;
      const selectedLaneIds = getSanitizedLaneSelectionForTier(tier);
      const laneLabelById = new Map(serviceLaneOptions.map((lane) => [lane.id, lane.label]));
      const laneLabels = selectedLaneIds
        .map((laneId) => laneLabelById.get(laneId))
        .filter((label): label is string => Boolean(label));

      upsertCheckoutLaneSelection({
        tierSkuId: tier.skuId,
        laneIds: selectedLaneIds,
        laneLabels,
      });
      setCommittedLaneSelectionsByTierSku((previous) => ({
        ...previous,
        [tier.skuId]: [...selectedLaneIds],
      }));
    },
    [getSanitizedLaneSelectionForTier, serviceLaneOptions],
  );

  const commitCurrentSelectionToCart = useCallback(() => {
    const tierToCommit = workingTier;
    if (!tierToCommit) return false;

    const nextItems = items.filter((item) => !currentServiceSkuIds.has(item.skuId));

    replaceItems([
      ...nextItems,
      { skuId: tierToCommit.skuId, quantity: 1 },
      ...selectedOptionalAddOnSkuIds.map((skuId) => ({ skuId, quantity: 1 })),
    ]);
    persistCheckoutLaneSelectionForTier(tierToCommit);
    return true;
  }, [
    currentServiceSkuIds,
    items,
    persistCheckoutLaneSelectionForTier,
    replaceItems,
    selectedOptionalAddOnSkuIds,
    workingTier,
  ]);

  const ensureSelectedTierThenGo = (path: string) => {
    if (!commitCurrentSelectionToCart()) return;
    navigate(path);
  };

  const handleSnapshotPrimaryAction = () => {
    if (!workingTier) return;
    if (isFocusedTierInCart) {
      ensureSelectedTierThenGo('/checkout');
      return;
    }
    commitCurrentSelectionToCart();
  };

  const handleToggleLane = (laneId: string) => {
    if (!workingTier || !workingLaneRule || workingLaneRule.mode === 'all') return;
    if (!workingLaneRule.allowedLaneIds.includes(laneId)) return;

    setLaneSelectionsByTierSku((prev) => {
      const current = sanitizeLaneSelection(workingLaneRule, prev[workingTier.skuId]);
      const alreadySelected = current.includes(laneId);
      let next: string[];

      if (alreadySelected) {
        next = current.filter((id) => id !== laneId);
      } else {
        if (!canSelectLane(workingLaneRule, current, laneId)) {
          return prev;
        }
        next = [...current, laneId];
      }

      return {
        ...prev,
        [workingTier.skuId]: next,
      };
    });
  };

  const handleAddToCartFromStep = () => {
    commitCurrentSelectionToCart();
  };

  const handleToggleOptionalAddOn = (addOn: PriceBookSku) => {
    const skuId = addOn.skuId;
    const parentCode = getAddOnParentCode(addOn);
    setSelectedOptionalAddOnSkuIds((previous) => {
      if (previous.includes(skuId)) {
        return previous.filter((value) => value !== skuId);
      }

      const next = previous.filter((value) => {
        const sku = getSkuById(value);
        return !sku || getAddOnParentCode(sku) !== parentCode;
      });

      return [...next, skuId];
    });
  };

  const handleOpenShareModal = () => {
    const tierToShare = workingTier;
    if (!tierToShare) return;

    const selectedLaneIds = getSanitizedLaneSelectionForTier(tierToShare);
    const nextShareUrl = buildSharedServiceConfigUrl({
      servicePath: '/services/managed-marketing',
      tierSkuId: tierToShare.skuId,
      laneIds: selectedLaneIds,
      addOnSkuIds: selectedOptionalAddOnSkuIds,
      createdAt: Date.now(),
    });

    setShareUrl(nextShareUrl);
    setShareModalOpen(true);
  };

  return (
    <>
      <FunnelShell>
      <Hero>
        <HeroTitle>{currentService.title}</HeroTitle>
      </Hero>

      <Layout>
        <LeftStepRail $hidden={hideLeftStepRail} aria-label="Managed Marketing build steps">
          <LeftStepList>
            {STEP_ITEMS.map((item) => (
              <li key={item.step}>
                <LeftStepButton
                  type="button"
                  $active={activeStep === item.step}
                  onClick={() => scrollToStep(item.step)}
                >
                  <LeftStepLabel $active={activeStep === item.step}>{`Step ${item.step}`}</LeftStepLabel>
                  <LeftStepTitle $active={activeStep === item.step}>{item.title}</LeftStepTitle>
                </LeftStepButton>
              </li>
            ))}
          </LeftStepList>
        </LeftStepRail>

        <Main>
          <IntroStepSection id="managed-marketing-step-1" ref={setStepSectionRef(1)}>
            <StepIntroLayout>
              <StepIntroHeader>
                <StepHeading>
                  <StepBadge>1</StepBadge>
                  <StepHeadingText>Pick Your Base Tier</StepHeadingText>
                </StepHeading>
                <p style={{ margin: 0 }}>
                  Think of this like choosing your package size. Swipe through the same tier stack
                  used on the home subscription section.
                </p>
              </StepIntroHeader>

              <StepIntroStack>
                <CardStack3D
                  plans={stackPlans}
                  initialIndex={initialStackIndex}
                  hidePrice
                  onChange={(plan) => {
                    setFocusedTierSkuId(plan.id);
                  }}
                />
              </StepIntroStack>
            </StepIntroLayout>

            {focusedTier ? (
              tierAtGlanceItems.length > 0 ? (
                <ProfileFocusWrap>
                  <ProfileFocusStage aria-live="polite">
                    <ProfileFocusItem
                      key={`${activeProfileIndex}-${tierAtGlanceItems[activeProfileIndex]}`}
                      $phase={profilePhase}
                    >
                      {tierAtGlanceItems[activeProfileIndex]}
                    </ProfileFocusItem>
                  </ProfileFocusStage>
                  <ProfileFocusDots aria-label="Tier highlights">
                    {tierAtGlanceItems.map((item, index) => (
                      <ProfileFocusDot
                        key={item}
                        type="button"
                        $active={index === activeProfileIndex}
                        aria-label={`Show highlight ${index + 1}`}
                        onClick={() => queueProfileIndex(index)}
                      />
                    ))}
                  </ProfileFocusDots>
                </ProfileFocusWrap>
              ) : null
            ) : null}

          </IntroStepSection>

          <CompactStepSection id="managed-marketing-step-2" ref={setStepSectionRef(2)}>
            <StepSplitLayout>
              <StepNarrative>
                <StepHeading>
                  <StepBadge>2</StepBadge>
                  <StepHeadingText>Build Your Lane Mix</StepHeadingText>
                </StepHeading>
                {workingTier ? (
                  <SelectedTierMetaRow>
                    <SelectedTierName>{selectedTier ? selectedTier.name : workingTier.name}</SelectedTierName>
                    {workingLaneRule ? (
                      <SlotsTagInline>
                        {workingLaneRule.mode === 'all'
                          ? 'All lanes included'
                          : `${workingSelectedLaneIds.length}/${workingLaneRule.maxSelections} lanes selected`}
                      </SlotsTagInline>
                    ) : null}
                  </SelectedTierMetaRow>
                ) : null}
                <p style={{ margin: 0 }}>
                  Pick which lanes to run inside your selected tier. Click a lane to select it and click
                  again to remove it. Lane limits are enforced per tier, so when you reach the tier
                  limit you will need to unselect one of the active lanes before choosing another.
                </p>
              </StepNarrative>

              <StepInteractiveColumn>
                {workingTier && workingLaneRule ? (
                  <>
                    {visibleLaneSections.length ? (
                      <>
                        {visibleLaneSections.map((section) => (
                          <LaneTypeSection key={section.type}>
                            <LaneTypeHeading>{section.label}</LaneTypeHeading>
                            <LaneGrid>
                              {section.lanes.map((lane) => {
                                const isSelected = workingSelectedLaneIds.includes(lane.id);
                                const isDisabled =
                                  workingLaneRule.mode === 'all'
                                    ? true
                                    : !isSelected &&
                                      !canSelectLane(workingLaneRule, workingSelectedLaneIds, lane.id);
                                return (
                                  <LaneButton
                                    key={lane.id}
                                    type="button"
                                    $selected={isSelected}
                                    disabled={isDisabled}
                                    onClick={() => handleToggleLane(lane.id)}
                                    aria-pressed={isSelected}
                                  >
                                    <LaneMeta>
                                      {lane.type === 'core'
                                        ? 'Core lane'
                                        : lane.type === 'advanced'
                                          ? 'Advanced lane'
                                          : 'Specialty lane'}
                                    </LaneMeta>
                                    <strong>{lane.label}</strong>
                                    <span>{lane.description || 'No description provided.'}</span>
                                  </LaneButton>
                                );
                              })}
                            </LaneGrid>
                          </LaneTypeSection>
                        ))}
                      </>
                    ) : (
                      <p style={{ margin: 0 }}>
                        No lane offerings are configured for this tier yet. Configure offerings in the admin Services page.
                      </p>
                    )}
                  </>
                ) : (
                  <p style={{ margin: 0 }}>
                    Select a tier first, then this step becomes interactive.
                  </p>
                )}
              </StepInteractiveColumn>
            </StepSplitLayout>
          </CompactStepSection>

          <StepSection id="managed-marketing-step-3" ref={setStepSectionRef(3)}>
            <StepNarrative>
              <StepHeading>
                <StepBadge>3</StepBadge>
                <StepHeadingText>Confirm Rules + Included Setup</StepHeadingText>
              </StepHeading>
              <p style={{ margin: 0 }}>
                Review what is included in every tier and what falls outside the service scope before checkout.
              </p>
            </StepNarrative>

            <MiniGrid>
              <FeatureCardStack>
                <FeatureStatusIcon src="/images/checkMark.png" alt="Included in every tier" />
                <InclusionFeatureCard>
                  <InclusionFeatureEyebrow>Included in every Managed Marketing tier</InclusionFeatureEyebrow>
                  <InclusionFeatureGrid>
                    {CONSISTENT_FEATURES.map((feature) => {
                      const isOpen = selectedInclusionItem === feature.publicLabel;
                      return (
                        <InclusionFeatureTile
                          key={feature.publicLabel}
                          type="button"
                          $selected={isOpen}
                          onClick={() =>
                            setSelectedInclusionItem((current) =>
                              current === feature.publicLabel ? '' : feature.publicLabel,
                            )
                          }
                        >
                          <InclusionFeatureHeader>
                            <InclusionFeaturePublic>{feature.publicLabel}</InclusionFeaturePublic>
                          </InclusionFeatureHeader>
                          {isOpen ? (
                            <InclusionFeatureDetail>{feature.details}</InclusionFeatureDetail>
                          ) : null}
                        </InclusionFeatureTile>
                      );
                    })}
                  </InclusionFeatureGrid>
                </InclusionFeatureCard>
              </FeatureCardStack>

              <FeatureCardStack>
                <FeatureStatusIcon src="/images/xOut.png" alt="Excluded from service" />
                <ExclusionFeatureCard>
                  <InclusionFeatureEyebrow>Service-level exclusions</InclusionFeatureEyebrow>
                  {serviceExclusions.length ? (
                    <>
                      <InclusionFeatureGrid>
                        {serviceExclusions.map((entry) => {
                          const isOpen = selectedExclusionItem === entry.code;
                          return (
                            <InclusionFeatureTile
                              key={entry.code}
                              type="button"
                              $selected={isOpen}
                              onClick={() =>
                                setSelectedExclusionItem((current) =>
                                  current === entry.code ? '' : entry.code,
                                )
                              }
                            >
                              <InclusionFeatureHeader>
                                <InclusionFeaturePublic>{entry.name}</InclusionFeaturePublic>
                              </InclusionFeatureHeader>
                              {isOpen ? (
                                <InclusionFeatureDetail>
                                  {entry.description || 'No additional exclusion details provided.'}
                                </InclusionFeatureDetail>
                              ) : null}
                            </InclusionFeatureTile>
                          );
                        })}
                      </InclusionFeatureGrid>
                    </>
                  ) : (
                    <InclusionFeatureDetail>
                      This managed marketing service does not have any exclusions configured yet.
                    </InclusionFeatureDetail>
                  )}
                </ExclusionFeatureCard>
              </FeatureCardStack>

            </MiniGrid>
          </StepSection>

          <CompactStepSection id="managed-marketing-step-4" ref={setStepSectionRef(4)}>
            <StepHeader>
              <StepHeading>
                <StepBadge>4</StepBadge>
                <StepHeadingText>Optional Add-ons</StepHeadingText>
              </StepHeading>
              <p style={{ margin: 0, maxWidth: '48rem' }}>
                Add-ons are optional upsells you can layer onto any managed marketing tier before
                checkout. These add-on tiers are independent from your base service tier, so you can
                mix a basic marketing package with a higher-tier add-on if it fits the engagement.
              </p>
            </StepHeader>

            {optionalAddOns.length ? (
              <AddOnGrid>
                {optionalAddOnFamilies.map((family) => {
                  const selectedVariation =
                    family.variations.find((addOn) =>
                      selectedOptionalAddOnCartItems.some((item) => item.skuId === addOn.skuId),
                    ) || family.variations[0];
                  return (
                    <AddOnFamilyCard key={family.parentCode}>
                      <div style={{ display: 'grid', gap: '0.55rem' }}>
                        <AddOnTitle>{family.parentName}</AddOnTitle>
                        <AddOnChipRow>
                          {family.variations.map((addOn) => {
                            const isActive = selectedVariation.skuId === addOn.skuId;
                            const variationLabel = getAddOnVariationLabel(addOn, family.parentName);
                            return (
                              <AddOnChip
                                key={addOn.skuId}
                                type="button"
                                $selected={isActive}
                                onClick={() => handleToggleOptionalAddOn(addOn)}
                                aria-pressed={isActive}
                              >
                                {variationLabel}
                              </AddOnChip>
                            );
                          })}
                        </AddOnChipRow>
                        <AddOnSelectionPanel>
                          <AddOnDescription>
                            {selectedVariation.description || 'No description provided.'}
                          </AddOnDescription>
                        </AddOnSelectionPanel>
                      </div>
                    </AddOnFamilyCard>
                  );
                })}
              </AddOnGrid>
            ) : (
              <p style={{ margin: 0 }}>
                No optional add-ons are configured for managed marketing right now.
              </p>
            )}
          </CompactStepSection>

          <CompactStepSection id="managed-marketing-step-5" ref={setStepSectionRef(5)}>
            <StepHeader>
              <StepHeading>
                <StepBadge>5</StepBadge>
                <StepHeadingText>Finalize + Checkout</StepHeadingText>
              </StepHeading>
              <p style={{ margin: 0 }}>
                Add this service package to cart or share the configuration.
              </p>
            </StepHeader>
            <TierActionRow>
              <PrimaryButton type="button" onClick={handleAddToCartFromStep}>
                Add to cart
              </PrimaryButton>
              <ShareButton type="button" onClick={handleOpenShareModal}>
                Share configuration
              </ShareButton>
            </TierActionRow>
          </CompactStepSection>

        </Main>

        <RightColumn>
          <SidePanel>
            <SidePanelTitle>Selection Snapshot</SidePanelTitle>
            <p style={{ margin: 0, fontWeight: 700 }}>
              {selectedTier ? selectedTier.name : focusedTier ? focusedTier.name : 'No tier selected'}
            </p>
            {workingTier ? (
              <SummaryList>
                <li>{getServiceHoursSummary(workingTier)}</li>
                <li>{getContractSummary(workingTier)}</li>
                <li>{getCadenceSummary(workingTier)}</li>
                {workingLaneRule?.mode === 'all' ? (
                  <li>Lane plan: all lanes included</li>
                ) : (
                  <li>
                    Lane plan: {workingSelectedLaneIds.length}/
                    {workingLaneRule?.mode === 'choose' ? workingLaneRule.maxSelections : 0} selected
                  </li>
                )}
                {selectedOptionalAddOnCartItems.length ? (
                  <li>
                    Add-ons: {selectedOptionalAddOnCartItems.map((item) => getSkuById(item.skuId)?.name ?? '').filter(Boolean).join(', ')}
                  </li>
                ) : null}
              </SummaryList>
            ) : (
              <p style={{ margin: 0 }}>Choose a tier to begin.</p>
            )}
            <p style={{ margin: 0 }}>
              Required onboarding is auto-attached to your marketing tier. Pricing appears in checkout.
            </p>
            <ActionStack>
              <PrimaryButton type="button" onClick={handleSnapshotPrimaryAction}>
                {isFocusedTierInCart ? 'Proceed to checkout' : 'Add to cart'}
              </PrimaryButton>
              <ShareButton type="button" onClick={handleOpenShareModal}>
                Share package
              </ShareButton>
            </ActionStack>
          </SidePanel>

          <OtherServicesRailCard>
            <RailSectionTitle>View Other Services</RailSectionTitle>
            <OtherServicesRailList>
              <OtherServiceCardLink $variant="platform" to="/services/platform-development">
                <OtherServiceCardTitle>Platform Development</OtherServiceCardTitle>
                <OtherServiceCardTeaser>
                  Product roadmap, integrations, and full engineering delivery.
                </OtherServiceCardTeaser>
              </OtherServiceCardLink>
              <OtherServiceCardLink $variant="assets" to="/services/assets-design">
                <OtherServiceCardTitle>Assets & Design</OtherServiceCardTitle>
                <OtherServiceCardTeaser>
                  Creative production, brand systems, and design execution.
                </OtherServiceCardTeaser>
              </OtherServiceCardLink>
            </OtherServicesRailList>
          </OtherServicesRailCard>
        </RightColumn>

        <MobileSnapshotFab title="Selection Snapshot" buttonLabel="Snapshot">
          <SidePanel>
            <SidePanelTitle>Selection Snapshot</SidePanelTitle>
            <p style={{ margin: 0, fontWeight: 700 }}>
              {selectedTier ? selectedTier.name : focusedTier ? focusedTier.name : 'No tier selected'}
            </p>
            {workingTier ? (
              <SummaryList>
                <li>{getServiceHoursSummary(workingTier)}</li>
                <li>{getContractSummary(workingTier)}</li>
                <li>{getCadenceSummary(workingTier)}</li>
                {workingLaneRule?.mode === 'all' ? (
                  <li>Lane plan: all lanes included</li>
                ) : (
                  <li>
                    Lane plan: {workingSelectedLaneIds.length}/
                    {workingLaneRule?.mode === 'choose' ? workingLaneRule.maxSelections : 0} selected
                  </li>
                )}
                {selectedOptionalAddOnCartItems.length ? (
                  <li>
                    Add-ons: {selectedOptionalAddOnCartItems.map((item) => getSkuById(item.skuId)?.name ?? '').filter(Boolean).join(', ')}
                  </li>
                ) : null}
              </SummaryList>
            ) : (
              <p style={{ margin: 0 }}>Choose a tier to begin.</p>
            )}
            <p style={{ margin: 0 }}>
              Required onboarding is auto-attached to your marketing tier. Pricing appears in checkout.
            </p>
            <ActionStack>
              <PrimaryButton type="button" onClick={handleSnapshotPrimaryAction}>
                {isFocusedTierInCart ? 'Proceed to checkout' : 'Add to cart'}
              </PrimaryButton>
              <ShareButton type="button" onClick={handleOpenShareModal}>
                Share package
              </ShareButton>
            </ActionStack>
          </SidePanel>

          <OtherServicesRailCard>
            <RailSectionTitle>View Other Services</RailSectionTitle>
            <OtherServicesRailList>
              <OtherServiceCardLink $variant="platform" to="/services/platform-development">
                <OtherServiceCardTitle>Platform Development</OtherServiceCardTitle>
                <OtherServiceCardTeaser>
                  Product roadmap, integrations, and full engineering delivery.
                </OtherServiceCardTeaser>
              </OtherServiceCardLink>
              <OtherServiceCardLink $variant="assets" to="/services/assets-design">
                <OtherServiceCardTitle>Assets & Design</OtherServiceCardTitle>
                <OtherServiceCardTeaser>
                  Creative production, brand systems, and design execution.
                </OtherServiceCardTeaser>
              </OtherServiceCardLink>
            </OtherServicesRailList>
          </OtherServicesRailCard>
        </MobileSnapshotFab>
      </Layout>

      <ShareCheckoutLinkModal
        open={shareModalOpen}
        shareUrl={shareUrl}
        onClose={() => setShareModalOpen(false)}
      />
      </FunnelShell>
      <UpsellShowcaseSection ref={upsellSectionRef}>
        <UpsellShowcaseInner>
          <UpsellShowcaseCopy>
            <UpsellShowcaseTitle>Need More Than a Standard Tier?</UpsellShowcaseTitle>
            <UpsellShowcaseLead>
              Custom scopes and multi-service builds are handled directly with our team. If your
              requirements are outside the standard package flow, start a consult and we will map
              the right delivery model.
            </UpsellShowcaseLead>
            <UpsellShowcaseCards>
              <UpsellShowcaseCard>
                <UpsellShowcaseActions>
                  <UpsellCtaPrimary to="/services/custom-projects">
                    Consult with our team
                  </UpsellCtaPrimary>
                </UpsellShowcaseActions>
              </UpsellShowcaseCard>
            </UpsellShowcaseCards>
          </UpsellShowcaseCopy>

          <UpsellShowcaseStackWrap>
            <UpsellShowcaseImage src="/images/CybardBlimp.png" alt="CyBard blimp" />
          </UpsellShowcaseStackWrap>
        </UpsellShowcaseInner>
      </UpsellShowcaseSection>
    </>
  );
}
